import type { TFunction } from "i18next";
import type { FieldError, FieldErrors, FieldValues, Resolver } from "react-hook-form";
import { match, P } from "ts-pattern";
import type { z } from "zod";

/**
 * A react-hook-form resolver for a contract schema: the form validates with exactly the Zod schema
 * the API validates with, and gets back the schema's output (trimmed, lower-cased, coerced).
 * Zod's own messages are English and technical, so each issue becomes a Dutch message by its code
 * (`validationMessage`). A dozen lines instead of `@hookform/resolvers`.
 */
export function zodResolver<S extends z.ZodType<FieldValues, FieldValues>>(
	schema: S,
	message: (issue: z.core.$ZodIssue) => string,
): Resolver<z.input<S>, unknown, z.output<S>> {
	return async (values) => {
		const result = await schema.safeParseAsync(values);
		if (result.success) return { values: result.data, errors: {} };
		const errors: Record<string, unknown> = {};
		for (const issue of result.error.issues) {
			// The first problem per field is the one to show.
			setOnce(errors, issue.path.length > 0 ? issue.path.map(String) : ["root"], {
				type: issue.code,
				message: message(issue),
			});
		}
		return { values: {}, errors: errors as FieldErrors<z.input<S>> };
	};
}

function setOnce(target: Record<string, unknown>, path: readonly string[], error: FieldError) {
	let node = target;
	for (const key of path.slice(0, -1)) {
		node[key] ??= {};
		node = node[key] as Record<string, unknown>;
	}
	const last = path.at(-1) ?? "root";
	node[last] ??= error;
}

/** A Zod issue in Dutch, from `common:validation.*`. */
export const validationMessage = (issue: z.core.$ZodIssue, t: TFunction): string =>
	match(issue)
		.with({ code: "too_small", origin: "string", minimum: 1 }, () => t("validation.required"))
		.with({ code: "too_small", origin: "string" }, (i) =>
			t("validation.tooShort", { count: Number(i.minimum) }),
		)
		.with({ code: "too_big", origin: "string" }, (i) => t("validation.tooLong", { count: Number(i.maximum) }))
		.with({ code: "too_small", origin: P.union("number", "int") }, (i) =>
			t("validation.tooSmall", { minimum: Number(i.minimum) }),
		)
		.with({ code: "too_big", origin: P.union("number", "int") }, (i) =>
			t("validation.tooBig", { maximum: Number(i.maximum) }),
		)
		.with({ code: "invalid_format", format: "email" }, () => t("validation.email"))
		.with({ code: "invalid_format", format: "url" }, () => t("validation.url"))
		.with({ code: "invalid_type", expected: P.union("number", "int") }, () => t("validation.number"))
		.with({ code: "invalid_value" }, () => t("validation.choice"))
		// A form's own check names its message: `ctx.addIssue({ code: "custom", params: { i18n: "money" } })`.
		.with({ code: "custom", params: { i18n: P.string } }, (i) => t(`validation.${i.params.i18n}` as never))
		.otherwise(() => t("validation.invalid"));
